import { SingleResourceLoadingState } from "../../common/store/stateHelpers";
import { PersonModel, PhoneNumberSettingsModel, EmailSettingsModel } from "../../common/models/CommonModels";
import { Reducer } from "redux";
import { UserWithSettingsModel, CompanyWithSettingsModel } from "../models/SettingsModels";
import { RegisteredPersonModel } from "../../admin/models/CompanyModels";
import { ReceivedGenerateApiKeyAction, ActionType as WidgetSettingsActionType } from "./WidgetSettingsStore";

export enum ActionType {
    RequestPersonalData = "SETTINGS/REQUEST_PERSONAL",
    ReceivedPersonalData = "SETTINGS/RECEIVED_PERSONAL",
    RequestCompanyData = "SETTINGS/REQUEST_COMPANY",
    ReceivedCompanyData = "SETTINGS/RECEIVED_COMPANY",
    ToggleEmailVisibilty = "SETINGS/TOGGLE_EMAIL",
    TogglePhoneNumberVisibility = "SETTINGS/TOGGLE_PHONE",
    EditUser = "SETTINGS/EDIT_USER",
    EditCompany = "SETTINGS/EDIT_COMPANY",
    DeleteUser = "SETTINGS/DELETE_USER",
    DeleteUserStarted = "SETTINGS/DELETE_USER_STARTED",
    AddPhoneNumber = "SETTINGS/COMPANY_ADD_PHONENUMBER",
    AddEmail = "SETTINGS/COMPANY_ADD_EMAIL",
    DeletePhoneNumber = "SETTINGS/COMPANY_DELETE_PHONENUMBER",
    DeleteEmail = "SETTINGS/COMPANY_DELETE_EMAIL"
}

interface RequestPersonalDataAction {
    type: ActionType.RequestPersonalData,
}

interface ReceivedPersonalDataAction {
    type: ActionType.ReceivedPersonalData;
    data: UserWithSettingsModel;
}

interface RequestCompanyDataAction {
    type: ActionType.RequestCompanyData
}

interface ReceivedCompanyDataAction {
    type: ActionType.ReceivedCompanyData;
    data?: CompanyWithSettingsModel;
}

export interface EditUserAction {
    type: ActionType.EditUser,
    user: UserWithSettingsModel
}

export interface EditCompanyAction {
    type: ActionType.EditCompany;
    company: CompanyWithSettingsModel;
}

export interface ToggleEmailVisibiltyAction {
    type: ActionType.ToggleEmailVisibilty,
    visible: boolean;
}

export interface TogglePhoneNumberVisibilityAction {
    type: ActionType.TogglePhoneNumberVisibility;
    visible: boolean;
}

export interface DeleteUserAction {
    type: ActionType.DeleteUser;
    user: RegisteredPersonModel;
}

export interface AddOrEditPhoneNumberAction {
    type: ActionType.AddPhoneNumber;
    data: PhoneNumberSettingsModel;
}

export interface AddOrEditEmailAction {
    type: ActionType.AddEmail;
    data: EmailSettingsModel;
}

export interface DeletePhoneNumberAction {
    type: ActionType.DeletePhoneNumber;
    data: PhoneNumberSettingsModel;
}

export interface DeleteEmailAction {
    type: ActionType.DeleteEmail;
    data: EmailSettingsModel;
}

export const actionsCreators = {
    requestPersonalData: () => <RequestPersonalDataAction>{ type: ActionType.RequestPersonalData },
    receivedPersonalData: (data: UserWithSettingsModel) => <ReceivedPersonalDataAction>{ type: ActionType.ReceivedPersonalData, data },
    requestCompanyData: () => <RequestCompanyDataAction>{ type: ActionType.RequestCompanyData },
    receivedCompanyData: (data?: CompanyWithSettingsModel) => <ReceivedCompanyDataAction>{ type: ActionType.ReceivedCompanyData, data },
    toggleEmailVisibility: (visible: boolean) => <ToggleEmailVisibiltyAction>{ type: ActionType.ToggleEmailVisibilty, visible },
    togglePhoneNumberVisibility: (visible: boolean) => <TogglePhoneNumberVisibilityAction>{ type: ActionType.TogglePhoneNumberVisibility, visible },
    editUser: (user: UserWithSettingsModel) => <EditUserAction>{ type: ActionType.EditUser, user },
    editCompany: (company: CompanyWithSettingsModel) => <EditCompanyAction>{ type: ActionType.EditCompany, company },
    deleteUser: (user: RegisteredPersonModel) => <DeleteUserAction>{ type: ActionType.DeleteUser, user },
    addOrEditPhoneNumber: (data: PhoneNumberSettingsModel) => <AddOrEditPhoneNumberAction>{ type: ActionType.AddPhoneNumber, data },
    addOrEditEmail: (data: EmailSettingsModel) => <AddOrEditEmailAction>{ type: ActionType.AddEmail, data },
    deletePhoneNumber: (data: PhoneNumberSettingsModel) => <DeletePhoneNumberAction>{ type: ActionType.DeletePhoneNumber, data },
    deleteEmail: (data: EmailSettingsModel) => <DeleteEmailAction>{ type: ActionType.DeleteEmail, data }
}

interface EmailVisibilty {
    emailVisible: boolean;
}

interface PhoneNumberVisibility {
    phoneVisibile: boolean;
}

export interface SettingsState {
    personal: SingleResourceLoadingState<UserWithSettingsModel>,
    company: SingleResourceLoadingState<CompanyWithSettingsModel>
}

const initialState: SettingsState = {
    personal: {
        isLoading: false,
        data: undefined
    },
    company: {
        isLoading: false,
        data: undefined
    }
}

type SettingsAction = RequestPersonalDataAction
| ReceivedPersonalDataAction
| RequestCompanyDataAction
| ReceivedCompanyDataAction
| ToggleEmailVisibiltyAction
| TogglePhoneNumberVisibilityAction
| ReceivedGenerateApiKeyAction;

export const reducer: Reducer<SettingsState> = (state: SettingsState, action: SettingsAction) => {
    switch (action.type) {
        case ActionType.RequestPersonalData:
            return {
                ...state,
                personal: {
                    ...state.personal,
                    isLoading: true,
                }
            };
        case ActionType.ReceivedPersonalData:
            return {
                ...state,
                personal: {
                    ...state.personal,
                    isLoading: false,
                    data: action.data
                }
            }
        case ActionType.RequestCompanyData:
            return {
                ...state,
                company: {
                    ...state.company,
                    isLoading: true
                }
            }
        case ActionType.ReceivedCompanyData:
            return {
                ...state,
                company: {
                    isLoading: false,
                    data: action.data
                }
            }
        case ActionType.ToggleEmailVisibilty:
            if (!state.personal.data) {
                break;
            }

            return {
                ...state,
                personal: {
                    ...state.personal,
                    data: {
                        ...state.personal.data,
                        contact: {
                            ...state.personal.data.contact,
                            emailVisible: action.visible
                        }
                    }
                }
            }
        case ActionType.TogglePhoneNumberVisibility:
            if (!state.personal.data) {
                break;
            }

            return {
                ...state,
                personal: {
                    ...state.personal,
                    data: {
                        ...state.personal.data,
                        contact: {
                            ...state.personal.data.contact,
                            phoneNumberVisible: action.visible
                        }
                    }
                }
            }
        case WidgetSettingsActionType.ReceivedGenerateApiKey:
            if (!state.company.data) {
                break;
            }

            return {
                ...state,
                company: {
                    ...state.company,
                    data : {
                        ...state.company.data,
                        apiKey: action.apiKey
                    }
                }
            }
        default:
            const exCheck: never = action;

    }

    return state || initialState;
}



// WEBPACK FOOTER //
// ./src/account/store/settingsStore.ts